import { PLATFORMS } from '@lib/utils/platforms';
import { formatDurationToString } from '@lib/utils/units';
import type { PostContentSummary, SocialPlatformModule } from '../../types';
import { youtubeTextProblem } from './limits';
import { YouTubeComposeFields } from './YouTubeComposeFields';

/** YouTube's `privacyStatus` vocabulary. `private` is the unpublished state: there is no draft. */
export type YouTubePrivacy = 'public' | 'unlisted' | 'private';

export const YOUTUBE_PRIVACY_LEVELS: readonly YouTubePrivacy[] = ['public', 'unlisted', 'private'];

/** `status.longUploadsStatus` as YouTube answers it for the channel. */
export type YouTubeLongUploads = 'allowed' | 'eligible' | 'disallowed' | 'longUploadsUnspecified';

/**
 * YouTube's exact post content, as the list returns it and edit takes it
 * back: the video's title, description and tags, its privacy, the two
 * self-declarations and the subscriber notification of the upload call.
 * `madeForKids` is a legal statement the user makes, so the API has no
 * default for it; it is optional here only so a new post can list it as
 * missing (a stored row always carries it). No kind (a vertical video under
 * three minutes becomes a Short on YouTube's side) and no draft.
 */
export type YouTubePostContent = {
  platform: 'youtube';
  title: string;
  description?: string;
  tags?: string[];
  privacy: YouTubePrivacy;
  madeForKids?: boolean;
  containsSyntheticMedia: boolean;
  notifySubscribers: boolean;
};

/**
 * What the channel may post, read live: its name as YouTube answers it now,
 * the duration cap with the `longUploads` status behind it (so the form can
 * say what lifts it), and whether the channel itself is declared made for
 * kids, which makes every video so whatever the post says.
 */
export type YouTubeAccountCapabilities = {
  platform: 'youtube';
  available: true;
  displayName: string;
  longUploads: YouTubeLongUploads;
  channelMadeForKids: boolean;
  limits: { maxDurationSeconds: number };
};

/** YouTube's cap for a channel without long uploads, the value the API falls back to as well. */
const DEFAULT_MAX_DURATION_SECONDS = 15 * 60;

const describeContent = (content: YouTubePostContent): PostContentSummary => ({
  headline: content.title.trim() || 'Untitled post',
  privacy: content.privacy,
  draft: false,
  variant: null,
});

const isPrivacy = (value: unknown): value is YouTubePrivacy =>
  YOUTUBE_PRIVACY_LEVELS.some((level) => level === value);

/** The stored shape before this client reads it; the API fills the defaults of the three flags at preflight. */
type YouTubePostContentWire = {
  platform?: string;
  title?: string;
  description?: string;
  tags?: string[];
  privacy?: string;
  madeForKids?: boolean;
  containsSyntheticMedia?: boolean;
  notifySubscribers?: boolean;
};

const parseContent = (raw: unknown): YouTubePostContent => {
  const content = raw as YouTubePostContentWire | null;
  if (content?.platform !== 'youtube') {
    throw new Error(`youtube post with content for ${String(content?.platform)}`);
  }
  return {
    platform: 'youtube',
    title: content.title ?? '',
    ...(content.description !== undefined && { description: content.description }),
    ...(content.tags !== undefined && { tags: content.tags }),
    privacy: isPrivacy(content.privacy) ? content.privacy : 'public',
    ...(content.madeForKids !== undefined && { madeForKids: content.madeForKids }),
    containsSyntheticMedia: content.containsSyntheticMedia === true,
    notifySubscribers: content.notifySubscribers !== false,
  };
};

/**
 * A public post with YouTube's own defaults on the switches and nothing the
 * user must declare: the title and the made-for-kids statement are theirs
 * to give, so the form lists both as missing rather than presuming.
 */
const defaultContent = (): YouTubePostContent => ({
  platform: 'youtube',
  title: '',
  privacy: 'public',
  containsSyntheticMedia: false,
  notifySubscribers: true,
});

/** The duration cap is the API's `duration_too_long` on submit, so capabilities add nothing here. */
const missingField = (content: YouTubePostContent): string | null => {
  const problem = youtubeTextProblem(content);
  if (problem) return problem;
  if (content.madeForKids === undefined) return 'Say whether this post is made for kids.';
  return null;
};

/** Fields drift by lifecycle stage on backend objects, hence the optionals. */
type YouTubeAccountCapabilitiesWire = {
  platform?: string;
  displayName?: string;
  longUploads?: string;
  channelMadeForKids?: boolean;
  limits?: { maxDurationSeconds?: number };
};

const isLongUploads = (value: unknown): value is YouTubeLongUploads =>
  value === 'allowed' ||
  value === 'eligible' ||
  value === 'disallowed' ||
  value === 'longUploadsUnspecified';

const parseCapabilities = (raw: unknown): YouTubeAccountCapabilities => {
  const wire = raw as YouTubeAccountCapabilitiesWire | null;
  if (wire?.platform !== 'youtube') {
    throw new Error(`youtube channel with capabilities for ${String(wire?.platform)}`);
  }
  return {
    platform: 'youtube',
    available: true,
    displayName: wire.displayName ?? '',
    longUploads: isLongUploads(wire.longUploads) ? wire.longUploads : 'longUploadsUnspecified',
    channelMadeForKids: wire.channelMadeForKids === true,
    limits: {
      maxDurationSeconds: wire.limits?.maxDurationSeconds ?? DEFAULT_MAX_DURATION_SECONDS,
    },
  };
};

const describeCapabilities = ({ limits, longUploads }: YouTubeAccountCapabilities): string => {
  const cap = `Media up to ${formatDurationToString(limits.maxDurationSeconds)}`;
  return longUploads === 'eligible'
    ? `${cap}. Verify your phone number on YouTube to post longer media.`
    : cap;
};

export const youtubeModule: SocialPlatformModule<YouTubePostContent, YouTubeAccountCapabilities> = {
  platform: 'youtube',
  label: PLATFORMS.youtube.displayName,
  icon: <img src={PLATFORMS.youtube.icon} alt="" />,
  colors: { bg: 'rgba(255, 0, 0, 0.16)', text: '#ff8a8a' },
  accountNoun: 'channel',
  disconnectNote: 'StarZero’s access to your Google account is withdrawn as well.',
  // FIXME(atlas #380): a stopgap while Google has the two YouTube scopes
  // under verification. Its consent page shows "Google hasn't verified this
  // app" with the way through behind "Advanced", which reads as phishing
  // without this line. Remove the note once the scopes are verified.
  connectNote:
    'Google has not reviewed StarZero’s YouTube access yet, so its consent page warns before you continue. Choose Advanced, then Go to starzero.ai.',
  noAccountsNote:
    'Your Google login shared no channel that StarZero can post to. Connect again, allowing both of Google’s checkboxes, with an account that has a channel.',
  parseContent,
  describeContent,
  defaultContent,
  missingField,
  parseCapabilities,
  describeCapabilities,
  ComposeFields: YouTubeComposeFields,
};
